import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import './index.scss';

const Hero: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 设置canvas大小
    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    setCanvasSize();

    // 粒子配置
    const particleCount = 80; // 减少粒子数量以提高性能
    const particles: { x: number; y: number; size: number; speedX: number; speedY: number; color: string }[] = [];

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 2 + 0.5,
        speedX: (Math.random() - 0.5) * 0.8, // 降低速度以提高性能
        speedY: (Math.random() - 0.5) * 0.8,
        color: `rgba(${Math.random() * 70 + 185}, ${Math.random() * 70 + 185}, ${Math.random() * 70 + 255}, ${Math.random() * 0.5 + 0.3})`
      });
    }

    // 优化绘制函数
    const draw = () => {
      // 使用clearRect而不是fillRect以提高性能
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle, index) => {
        // 绘制粒子
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = particle.color;
        ctx.fill();

        // 更新粒子位置
        particle.x += particle.speedX;
        particle.y += particle.speedY;

        // 边界检测
        if (particle.x < 0 || particle.x > canvas.width) {
          particle.speedX = -particle.speedX;
        }
        if (particle.y < 0 || particle.y > canvas.height) {
          particle.speedY = -particle.speedY;
        }

        // 绘制粒子之间的连线（仅对近距离粒子）
        for (let j = index + 1; j < particles.length; j++) {
          const dx = particles[j].x - particle.x;
          const dy = particles[j].y - particle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 80) { // 减少连线距离以提高性能
            ctx.beginPath();
            ctx.strokeStyle = `rgba(66, 153, 225, ${0.15 * (1 - distance / 80)})`;
            ctx.lineWidth = 0.3; // 减少线宽以提高性能
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      });

      requestAnimationFrame(draw);
    };

    // 处理窗口大小变化
    const handleResize = () => {
      setCanvasSize();
    };

    // 节流处理窗口大小变化
    let resizeTimeout: ReturnType<typeof setTimeout>;
    const throttledResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 200);
    };

    window.addEventListener('resize', throttledResize);
    draw();

    return () => {
      window.removeEventListener('resize', throttledResize);
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, []);

  return (
    <section className="hero">
      <canvas ref={canvasRef} className="hero-canvas" />
      <div className="hero-content">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="hero-text"
        >
          <h1>
            <span className="hero-title">你好，我是一名个人 <span className="hero-name">开发者</span></span>
            <br />
            <span className="hero-subtitle">探索技术的无限可能</span>
          </h1>
          <p className="hero-description">
            专注于前端、后端开发和技术分享，
            记录学习历程，分享技术见解。
          </p>
          <div className="hero-buttons">
            <motion.a 
              href="/articles"
              className="btn-primary"
              whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(66, 153, 225, 0.6)' }}
              whileTap={{ scale: 0.95 }}
            >
              浏览文章
            </motion.a>
            <motion.a 
              href="/about"
              className="btn-secondary"
              whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(102, 126, 234, 0.6)' }}
              whileTap={{ scale: 0.95 }}
            >
              了解更多
            </motion.a>
          </div>
        </motion.div>
      </div>
      <div className="hero-glow"></div>
    </section>
  );
};

export default Hero;